import api from './api';

// Campaign service to handle API requests for campaigns
export const campaignService = {
  // Get all campaigns
  getCampaigns: async () => {
    try {
      const response = await api.get('/api/campaigns');
      return response.data;
    } catch (error) {
      console.error('Error fetching campaigns:', error);
      throw error;
    }
  },

  // Get a single campaign by ID with pagination support
  getCampaign: async (id, offset = null, limit = null, tab = 'companies', search = '', compliance_filter = null) => {
    try {
      const params = { tab, search };
      if (typeof offset === 'number') {
        params.offset = offset;
      }
      if (typeof limit === 'number') {
        params.limit = limit;
      }
      if (compliance_filter && compliance_filter !== 'all') {
        params.compliance_filter = compliance_filter;
      }
      const response = await api.get(`/api/campaigns/${id}`, {
        params
      });
      return response.data;
    } catch (error) {
      console.error(`Error fetching campaign ${id}:`, error);
      throw error;
    }
  },

  // Delete a campaign
  deleteCampaign: async (id) => {
    try {
      const response = await api.delete(`/api/campaigns/${id}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting campaign ${id}:`, error);
      throw error;
    }
  },

  // Update a campaign's metadata
  updateCampaign: async (id, updates) => {
    try {
      const response = await api.patch(`/api/campaigns/${id}`, updates);
      return response.data;
    } catch (error) {
      console.error(`Error updating campaign ${id}:`, error);
      throw error;
    }
  },

  // Remove entities (companies or contacts) from a campaign version
  removeEntities: async (campaignId, entityIds, versionId, entityType) => {
    try {
      const response = await api.post(`/api/versioning/${campaignId}/remove-entities`, {
        entity_ids: entityIds,
        version_id: versionId,
        entity_type: entityType
      });
      return response.data;
    } catch (error) {
      console.error(`Error removing entities from campaign ${campaignId}:`, error);
      throw error;
    }
  },

  // Export campaign data to excel
  exportToXlsx: async (campaignId, versionId) => {
    try {
      const response = await api.get(`/api/campaigns/${campaignId}/export`, {
        params: {
          version_id: versionId
        },
        responseType: 'blob',
      });
      // Return both data and headers so we can extract the filename
      return {
        data: response.data,
        headers: response.headers
      };
    } catch (error) {
      console.error(`Error exporting campaign ${campaignId}:`, error);
      throw error;
    }
  },

  // Custom fields methods
  approveCustomFieldGeneration: async (campaignId, configId) => {
    // Note: This returns an SSE stream URL, handle with fetchEventSource
    return `/api/custom-fields/campaigns/${campaignId}/configs/${configId}/approve/stream`;
  },

  updateCustomFieldValue: async (campaignId, fieldId, membershipId, value, entityType = 'company') => {
    try {
      // Convert plural to singular for API (companies -> company, contacts -> contact)
      const apiEntityType = entityType === 'companies' ? 'company' : entityType === 'contacts' ? 'contact' : entityType;
      
      const response = await api.put(
        `/api/custom-fields/campaigns/${campaignId}/fields/${fieldId}/values/${membershipId}`,
        { value, entity_type: apiEntityType }
      );
      return response.data;
    } catch (error) {
      console.error(`Error updating custom field value:`, error);
      throw error;
    }
  },

  deleteCustomField: async (campaignId, fieldId) => {
    try {
      const response = await api.delete(`/api/custom-fields/campaigns/${campaignId}/fields/${fieldId}`);
      return response.data;
    } catch (error) {
      console.error(`Error deleting custom field ${fieldId}:`, error);
      throw error;
    }
  },

  resetDraftConfig: async (campaignId, configId) => {
    try {
      const response = await api.delete(
        `/api/custom-fields/campaigns/${campaignId}/configs/${configId}/reset`
      );
      return response.data;
    } catch (error) {
      console.error(`Error resetting draft config:`, error);
      throw error;
    }
  },

  // Custom field generation methods
  generateCustomFields: async (campaignId, prompt, configId = null) => {
    // Returns SSE stream URL for custom field generation
    return `/api/custom-fields/campaigns/${campaignId}/fields/generate/stream`;
  },

  rejectCustomFieldPreview: async (campaignId, configId) => {
    try {
      const response = await api.delete(
        `/api/custom-fields/campaigns/${campaignId}/configs/${configId}/reset`
      );
      return response.data;
    } catch (error) {
      console.error(`Error rejecting custom field preview:`, error);
      throw error;
    }
  },

  // Reversion methods
  revertToVersion: async (campaignId, versionId) => {
    try {
      const response = await api.post(
        `/api/versioning/${campaignId}/revert-from-message`,
        { version_id: versionId }
      );
      return response.data;
    } catch (error) {
      console.error(`Error reverting campaign ${campaignId} to version ${versionId}:`, error);
      throw error;
    }
  },

  // ─── Message Drafting ─────────────────────────────────────────────────

  createMessageDraft: async (campaignId, data) => {
    try {
      const response = await api.post(
        `/api/message-drafting/campaigns/${campaignId}/drafts`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error creating message draft:', error);
      throw error;
    }
  },

  updateMessageDraft: async (campaignId, groupId, data) => {
    try {
      const response = await api.put(
        `/api/message-drafting/campaigns/${campaignId}/drafts/${groupId}`,
        data
      );
      return response.data;
    } catch (error) {
      console.error('Error updating message draft:', error);
      throw error;
    }
  },

  deleteMessageDraft: async (campaignId, groupId) => {
    try {
      const response = await api.delete(
        `/api/message-drafting/campaigns/${campaignId}/drafts/${groupId}`
      );
      return response.data;
    } catch (error) {
      console.error('Error deleting message draft:', error);
      throw error;
    }
  },

  renameMessageDraft: async (campaignId, groupId, emailName) => {
    try {
      const response = await api.patch(
        `/api/message-drafting/campaigns/${campaignId}/drafts/${groupId}/rename`,
        { email_name: emailName }
      );
      return response.data;
    } catch (error) {
      console.error('Error renaming message draft:', error);
      throw error;
    }
  },

  updateMessageDraftDescription: async (campaignId, groupId, description) => {
    try {
      const response = await api.patch(
        `/api/message-drafting/campaigns/${campaignId}/drafts/${groupId}/description`,
        { description }
      );
      return response.data;
    } catch (error) {
      console.error('Error updating message draft description:', error);
      throw error;
    }
  },

  previewMessageDraft: async (campaignId, subjectTemplate, bodyTemplate) => {
    try {
      const response = await api.post(
        `/api/message-drafting/campaigns/${campaignId}/preview`,
        { subject_template: subjectTemplate, body_template: bodyTemplate }
      );
      return response.data;
    } catch (error) {
      console.error('Error previewing message draft:', error);
      throw error;
    }
  },

  // ─── AI Draft — emails_api (/api/emails) ─────────────────────────────────

  /**
   * Returns the SSE URL for AI scratch generation.
   * Caller is responsible for opening the stream via useSSEStream / fetchEventSource.
   * POST body: { email_name, type_of_message, message_type, prompt, body: null, enable_html,
   *   reuse_existing_config_id?: string } — omit for a new column; set when regenerating preview for an edited draft.
   */
  generateMessageDraft: (campaignId) =>
    `/api/emails/${campaignId}/draft/generate/stream`,

  /**
   * Returns the SSE URL for approving a draft config (full fan-out).
   * Caller POSTs with no body; the backend rebuilds params from the stored config.
   */
  approveDraftConfig: (campaignId, configId) =>
    `/api/emails/${campaignId}/draft/configs/${configId}/approve/stream`,

  /**
   * Returns the active in-flight or ready-to-approve AI draft config for a campaign.
   * Response: { phase: 'idle'|'generating'|'ready'|'approving', config_id, completion_percentage }
   * Used by MessageDraftingPanel on mount to restore state after network interruptions.
   */
  getActiveDraftConfig: async (campaignId) => {
    try {
      const response = await api.get(`/api/emails/${campaignId}/draft/active-config`);
      return response.data;
    } catch (error) {
      console.error('Error fetching active draft config:', error);
      throw error;
    }
  },

  /**
   * Returns the SSE URL for reconnecting to an in-progress draft generation or approval.
   * The stream emits the same events as generate/stream and approve/stream.
   * Safe to open even if the operation has already completed — exits immediately with 100 % status.
   */
  observeDraftConfigStream: (campaignId, configId) =>
    `/api/emails/${campaignId}/draft/configs/${configId}/observe/stream`,

  /**
   * Hard-deletes a draft config and all associated field definitions / values.
   * Call before Discard or Regenerate.
   */
  deleteDraftConfig: async (campaignId, configId) => {
    try {
      const response = await api.delete(
        `/api/emails/${campaignId}/draft/configs/${configId}`
      );
      return response.data;
    } catch (error) {
      console.error(`Error deleting draft config ${configId}:`, error);
      throw error;
    }
  },

  /**
   * Reject an email draft preview — deletes config, definitions, and values
   * so the preview columns disappear from the table.
   */
  rejectDraftPreview: async (campaignId, configId) => {
    try {
      const response = await api.delete(
        `/api/custom-fields/campaigns/${campaignId}/configs/${configId}/reset`
      );
      return response.data;
    } catch (error) {
      console.error(`Error rejecting draft preview ${configId}:`, error);
      throw error;
    }
  },

  /**
   * Discard AI preview while **reusing** an existing draft column: restore definitions,
   * config row, and cell values from the pre-preview snapshot (see recycle flow).
   */
  restoreMessageDraftPreview: async (campaignId, configId) => {
    try {
      const response = await api.post(
        `/api/emails/${campaignId}/draft/configs/${configId}/discard-preview`
      );
      return response.data;
    } catch (error) {
      console.error(`Error restoring draft preview ${configId}:`, error);
      throw error;
    }
  },

  polishBody: async (bodyTemplate, typeOfMessage = 'email') => {
    try {
      const response = await api.post('/api/message-drafting/polish', {
        body_template: bodyTemplate,
        type_of_message: typeOfMessage,
      });
      return response.data;
    } catch (error) {
      console.error('Error polishing message body:', error);
      throw error;
    }
  },

  getMessageDrafts: async (campaignId) => {
    try {
      const response = await api.get(
        `/api/message-drafting/campaigns/${campaignId}/drafts`
      );
      return response.data;
    } catch (error) {
      console.error('Error fetching message drafts:', error);
      throw error;
    }
  },

  // ─── User Signatures (message drafting) ─────────────────────────────────

  getSignatures: async () => {
    try {
      const response = await api.get('/api/user/signatures');
      return response.data;
    } catch (error) {
      console.error('Error fetching signatures:', error);
      throw error;
    }
  },

  updateSignatures: async (payload) => {
    try {
      const response = await api.put('/api/user/signatures', payload);
      return response.data;
    } catch (error) {
      console.error('Error updating signatures:', error);
      throw error;
    }
  },

  deleteSignature: async (signatureId) => {
    try {
      const response = await api.delete(`/api/user/signatures/${encodeURIComponent(signatureId)}`);
      return response.data;
    } catch (error) {
      console.error('Error deleting signature:', error);
      throw error;
    }
  },

  getExportTracking: async (campaignId) => {
    try {
      const response = await api.get(
        '/api/integrations/export-tracking',
        { params: { campaign_id: campaignId } }
      );
      return response.data;
    } catch (error) {
      // Non-critical — table still renders without export badges
      console.error('Error fetching export tracking:', error);
      return { exports: [] };
    }
  },

  // Contact enrichment methods
  enrichContacts: async (campaignId, versionId, contactIds = null) => {
    try {
      const response = await api.post(
        `/api/enrichment/campaigns/${campaignId}/contacts/enrich`,
        {
          version_id: versionId,
          contact_ids: contactIds
        }
      );
      return response.data;
    } catch (error) {
      console.error(`Error enriching contacts for campaign ${campaignId}:`, error);
      throw error;
    }
  }
};

export default campaignService; 